import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { chargerRequestsApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Clock, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';
import {
  Page, GlassCard, SectionHeading, EmptyState, ErrorState, PageSkeleton, Field, DetailRows,
  RequestStatusBadge, ConfirmDialog, errorMessage, humanize
} from '@/components/glass/surfaces';

const editFields = [
  ['charger_name', 'Charger name', 'text'],
  ['charge_point_id', 'OCPP charger ID', 'text'],
  ['charger_type', 'Type / model', 'text'],
  ['capacity_kw', 'Capacity (kW)', 'number'],
  ['connector_count', 'Connector count', 'number'],
  ['location', 'Location', 'text'],
  ['zone', 'Zone', 'text'],
  ['site_name', 'Site', 'text']
];

const readFields = (request) => ([
  ['Location', request.location],
  ['Zone', request.zone],
  ['Site', request.site_name],
  ['Type', request.charger_type],
  ['Capacity', request.capacity_kw ? `${request.capacity_kw} kW` : '-'],
  ['Connectors', request.connector_count],
  ['Revision', request.revision],
  ['Updated', formatDate(request.updated_at)]
]);

const objectEntries = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? Object.entries(value) : []
);

const ChargerRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const [request, setRequest] = useState(null);
  const [activity, setActivity] = useState({ comments: [], audit: [] });
  const [attachments, setAttachments] = useState([]);
  const [comment, setComment] = useState('');
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [acting, setActing] = useState(false);
  const [pending, setPending] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    setRequest(null);
    setEditData(null);
    setActivity({ comments: [], audit: [] });
    setAttachments([]);
    setNotFound(false);
    setError('');
    setLoading(true);
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [requestResponse, activityResponse, attachmentResponse] = await Promise.all([
          chargerRequestsApi.getById(id),
          chargerRequestsApi.activity(id),
          chargerRequestsApi.attachments(id)
        ]);
        if (cancelled) return;
        const next = requestResponse.data;
        setRequest(next);
        setEditData({
          charger_name: next.charger_name,
          charge_point_id: next.charge_point_id,
          charger_type: next.charger_type || '',
          capacity_kw: next.capacity_kw ?? '',
          connector_count: next.connector_count,
          location: next.location,
          zone: next.zone || '',
          site_name: next.site_name || '',
          technical_information: JSON.stringify(next.technical_information || {}, null, 2),
          commercial_information: JSON.stringify(next.commercial_information || {}, null, 2)
        });
        setActivity(activityResponse.data || { comments: [], audit: [] });
        setAttachments(attachmentResponse.data || []);
        setError('');
        setNotFound(false);
      } catch (fetchError) {
        if (cancelled) return;
        if (fetchError.response?.status === 404) {
          setNotFound(true);
          setRequest(null);
          setError('');
        } else {
          setError(errorMessage(fetchError, 'Failed to load request'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id, reloadToken]);

  const runAction = async (fn, success) => {
    setActing(true);
    try {
      await fn();
      toast.success(success);
      setReloadToken((value) => value + 1);
      return true;
    } catch (actionError) {
      toast.error(errorMessage(actionError, 'Action failed'));
      return false;
    } finally {
      setActing(false);
    }
  };

  const save = () => {
    let technicalInformation;
    let commercialInformation;
    try {
      technicalInformation = JSON.parse(editData.technical_information || '{}');
      commercialInformation = JSON.parse(editData.commercial_information || '{}');
    } catch (parseError) {
      toast.error('Technical and commercial information must be valid JSON');
      return;
    }
    if (!editData.charger_name.trim() || !editData.charge_point_id.trim() || !editData.location.trim()) {
      toast.error('Name, OCPP ID, and location are required');
      return;
    }
    if (/\s/.test(editData.charge_point_id.trim())) {
      toast.error('OCPP charger ID cannot contain spaces');
      return;
    }
    runAction(() => chargerRequestsApi.update(id, {
      ...editData,
      charger_name: editData.charger_name.trim(),
      charge_point_id: editData.charge_point_id.trim(),
      location: editData.location.trim(),
      capacity_kw: editData.capacity_kw === '' ? null : Number(editData.capacity_kw),
      connector_count: Number(editData.connector_count) || 1,
      technical_information: technicalInformation,
      commercial_information: commercialInformation
    }), 'Request updated');
  };

  const download = async (item) => {
    try {
      const response = await chargerRequestsApi.download(id, item.id);
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = item.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (downloadError) {
      toast.error(errorMessage(downloadError, 'Failed to download attachment'));
    }
  };

  const ask = (config) => {
    if (acting) return;
    if (!comment.trim()) {
      toast.error('Add a comment first');
      return;
    }
    setPending(config);
  };

  if ((request && request.id !== id) || (loading && !request)) {
    return <PageSkeleton testId="charger-request-loading" cards={2} />;
  }

  if (notFound) {
    return (
      <Page testId="charger-request-missing">
        <ErrorState
          title="Request not found"
          message="This charger request is unavailable, or you do not have access to it."
          onRetry={() => navigate('/charger-requests')}
          retryLabel="Back to requests"
        />
      </Page>
    );
  }

  if (!request && error) {
    return (
      <Page testId="charger-request-detail">
        <ErrorState message={error} onRetry={() => { setLoading(true); setReloadToken((value) => value + 1); }} />
      </Page>
    );
  }

  const editable = can('requests.edit') && ['draft', 'needs_modification'].includes(request.status) && editData;
  const isReviewer = request.assigned_company_id === user?.company_id || user?.role === 'vdrive_super_admin';
  const showReview = can('requests.review') && request.status === 'pending_approval' && isReviewer;
  const canComment = can('requests.comment');
  const technicalEntries = objectEntries(request.technical_information);
  const commercialEntries = objectEntries(request.commercial_information);

  return (
    <Page testId="charger-request-detail">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Button variant="ghost" size="icon" className="mt-1 shrink-0 rounded-full" asChild>
            <Link to="/charger-requests" aria-label="Back to charger requests">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="break-words text-3xl font-semibold tracking-tight">{request.charger_name}</h1>
              <RequestStatusBadge status={request.status} />
            </div>
            <p className="mt-1 break-all font-mono text-sm text-muted-foreground">{request.charge_point_id}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {can('requests.submit') && ['draft', 'needs_modification'].includes(request.status) ? (
            <Button
              type="button"
              className="rounded-full"
              disabled={acting}
              onClick={() => setPending({
                title: 'Send this request for approval?',
                description: 'Reviewers at the assigned company will be able to approve, reject, or ask for changes.',
                confirmLabel: 'Send for approval',
                run: () => runAction(() => chargerRequestsApi.submit(id), 'Request submitted')
              })}
            >
              Send for Approval
            </Button>
          ) : null}
          {can('requests.install') && request.status === 'approved_ready_installation' ? (
            <Button type="button" className="rounded-full" disabled={acting} onClick={() => runAction(() => chargerRequestsApi.startInstallation(id), 'Installation started')}>
              Start Installation
            </Button>
          ) : null}
          {can('requests.install') && request.status === 'installation_in_progress' ? (
            <Button
              type="button"
              className="rounded-full"
              disabled={acting}
              onClick={() => setPending({
                title: 'Mark installation complete?',
                description: 'This records the charger as installed. The status cannot be moved backward from this screen.',
                confirmLabel: 'Complete installation',
                run: () => runAction(() => chargerRequestsApi.completeInstallation(id), 'Installation completed')
              })}
            >
              Complete Installation
            </Button>
          ) : null}
          {request.linked_charge_point_id ? (
            <Button type="button" variant="outline" className="rounded-full" asChild>
              <Link to={`/chargers/${request.linked_charge_point_id}`}>Open Charger</Link>
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <ErrorState title="Could not refresh this request" message={error} onRetry={() => setReloadToken((value) => value + 1)} /> : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <GlassCard className="p-5 sm:p-6 lg:col-span-2">
          <SectionHeading title="Request details" description={editable ? 'This draft can still be edited.' : 'Submitted details are locked until a reviewer asks for changes.'} />
          {editable ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {editFields.map(([key, label, type]) => (
                <Field label={label} key={key}>
                  <Input
                    type={type}
                    min={key === 'connector_count' ? 1 : undefined}
                    max={key === 'connector_count' ? 20 : undefined}
                    value={editData[key]}
                    onChange={(event) => setEditData({ ...editData, [key]: event.target.value })}
                    className="rounded-xl"
                  />
                </Field>
              ))}
              <Field className="sm:col-span-2" label="Technical information (JSON)">
                <Textarea
                  value={editData.technical_information}
                  onChange={(event) => setEditData({ ...editData, technical_information: event.target.value })}
                  className="min-h-32 rounded-xl font-mono text-xs"
                />
              </Field>
              <Field className="sm:col-span-2" label="Commercial information (JSON)">
                <Textarea
                  value={editData.commercial_information}
                  onChange={(event) => setEditData({ ...editData, commercial_information: event.target.value })}
                  className="min-h-32 rounded-xl font-mono text-xs"
                />
              </Field>
              <div className="sm:col-span-2">
                <Button type="button" className="rounded-full" onClick={save} disabled={acting}>
                  {acting ? 'Saving...' : 'Save draft'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-3 sm:grid-cols-2">
                {readFields(request).map(([label, value]) => (
                  <div key={label} className="rounded-2xl bg-black/[0.03] px-4 py-3 dark:bg-white/[0.04]">
                    <div className="text-xs text-muted-foreground">{label}</div>
                    <div className="mt-1 break-words font-medium">{value || '-'}</div>
                  </div>
                ))}
              </div>
              {technicalEntries.length > 0 ? (
                <div>
                  <h3 className="mb-2 text-sm font-medium">Technical information</h3>
                  <DetailRows rows={technicalEntries.map(([key, value]) => ({ label: humanize(key), value: typeof value === 'object' ? JSON.stringify(value) : String(value) }))} />
                </div>
              ) : null}
              {commercialEntries.length > 0 ? (
                <div>
                  <h3 className="mb-2 text-sm font-medium">Commercial information</h3>
                  <DetailRows rows={commercialEntries.map(([key, value]) => ({ label: humanize(key), value: typeof value === 'object' ? JSON.stringify(value) : String(value) }))} />
                </div>
              ) : null}
            </div>
          )}
        </GlassCard>

        <GlassCard className="p-5 sm:p-6">
          <SectionHeading title="Inspection" description="Comments are stored with the request history." />
          {showReview || canComment ? (
            <div className="space-y-3">
              <Textarea
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder={showReview ? 'Review comment (required)' : 'Add a comment'}
                className="min-h-28 rounded-xl"
              />
              {showReview ? (
                <div className="grid gap-2">
                  <Button
                    type="button"
                    className="rounded-full"
                    disabled={acting}
                    onClick={() => ask({
                      title: 'Approve this request?',
                      description: 'Approval creates the charger record for this OCPP ID and moves the request to installation.',
                      confirmLabel: 'Approve',
                      run: async () => {
                        const ok = await runAction(() => chargerRequestsApi.review(id, { decision: 'approved', comment: comment.trim() }), 'Request approved');
                        if (ok) setComment('');
                        return ok;
                      }
                    })}
                  >
                    Approve
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full"
                    disabled={acting}
                    onClick={() => ask({
                      title: 'Send this back for changes?',
                      description: 'The requester can edit the draft and submit it again.',
                      confirmLabel: 'Request changes',
                      run: async () => {
                        const ok = await runAction(() => chargerRequestsApi.review(id, { decision: 'needs_modification', comment: comment.trim() }), 'Changes requested');
                        if (ok) setComment('');
                        return ok;
                      }
                    })}
                  >
                    Needs Changes
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    className="rounded-full"
                    disabled={acting}
                    onClick={() => ask({
                      title: 'Reject this request?',
                      description: 'The request is closed as rejected. This comment is stored with the decision.',
                      confirmLabel: 'Reject request',
                      destructive: true,
                      run: async () => {
                        const ok = await runAction(() => chargerRequestsApi.review(id, { decision: 'rejected', comment: comment.trim() }), 'Request rejected');
                        if (ok) setComment('');
                        return ok;
                      }
                    })}
                  >
                    Reject
                  </Button>
                </div>
              ) : null}
              {canComment ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full rounded-full"
                  disabled={acting || !comment.trim()}
                  onClick={async () => {
                    const text = comment.trim();
                    if (!text) {
                      toast.error('Write a comment first');
                      return;
                    }
                    const ok = await runAction(() => chargerRequestsApi.comment(id, { comment: text, note_type: 'comment' }), 'Comment added');
                    if (ok) setComment('');
                  }}
                >
                  Add Comment
                </Button>
              ) : null}
              {canComment ? (
                <Field label="Attachment">
                  <Input
                    key={fileInputKey}
                    type="file"
                    className="cursor-pointer rounded-xl"
                    onChange={async (event) => {
                      const file = event.target.files?.[0];
                      setFileInputKey((value) => value + 1);
                      if (!file) return;
                      await runAction(() => chargerRequestsApi.upload(id, file), 'Attachment uploaded');
                    }}
                  />
                </Field>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">You can follow this request, but your role cannot comment or review it.</p>
          )}
        </GlassCard>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <GlassCard className="p-5 sm:p-6">
          <SectionHeading title="Comments and files" />
          {activity.comments.length === 0 && attachments.length === 0 ? (
            <EmptyState compact icon={Paperclip} title="Nothing here yet" description="Review notes and uploaded files will be listed here." />
          ) : (
            <div className="space-y-3">
              {activity.comments.map((item) => (
                <div key={item.id} className="rounded-2xl bg-black/[0.03] p-3 dark:bg-white/[0.04]">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-medium">{item.author_name || 'Unknown'}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(item.created_at)}</p>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm">{item.comment}</p>
                </div>
              ))}
              {attachments.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-2xl bg-black/[0.03] px-3 py-3 text-left text-sm font-medium text-primary hover:underline dark:bg-white/[0.04]"
                  onClick={() => download(item)}
                >
                  <Paperclip className="h-4 w-4 shrink-0" />
                  <span className="truncate">{item.filename}</span>
                </button>
              ))}
            </div>
          )}
        </GlassCard>

        <GlassCard className="p-5 sm:p-6">
          <SectionHeading title="Audit timeline" />
          {activity.audit.length === 0 ? (
            <EmptyState compact icon={Clock} title="No activity yet" description="Submissions, reviews, and installation steps are recorded here." />
          ) : (
            <ol className="space-y-4">
              {activity.audit.map((item) => (
                <li key={item.id} className="border-l-2 border-border pl-4">
                  <p className="font-medium">{humanize(item.action)}</p>
                  <p className="text-sm text-muted-foreground">{item.actor_name || 'System'} · {formatDate(item.created_at)}</p>
                </li>
              ))}
            </ol>
          )}
        </GlassCard>
      </div>

      <ConfirmDialog
        open={!!pending}
        title={pending?.title || ''}
        description={pending?.description || ''}
        confirmLabel={pending?.confirmLabel}
        destructive={pending?.destructive}
        loading={acting}
        onOpenChange={(open) => { if (!open) setPending(null); }}
        onConfirm={async () => {
          const ok = await pending?.run?.();
          if (ok) setPending(null);
        }}
        testId="confirm-request-action"
      />
    </Page>
  );
};

export default ChargerRequestDetailPage;
